import classNames from 'classnames';
import { useId } from 'react';
import { isEmail, ReactMultiEmail } from 'react-multi-email';
import { t } from 'ttag';
import ErrorMessage from '../../errorMessage';
import { SmallCloseIcon } from '../assets';
import './styles.scss';
import type { MultiEmailInputV2Props } from './types';
import { useMultiEmailInputV2 } from './useMultiEmailInputV2';

const ReactMultiEmailAny: any = ReactMultiEmail;

// WCAG "visually hidden" pattern: kept in the accessibility tree (unlike display:none) so
// htmlFor still resolves to a real accessible name when no visible title is given.
const VISUALLY_HIDDEN_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/**
 * Tag-style input for collecting a list of recipient email addresses. Runs useMultiEmailInputV2 for its side effects, renders ReactMultiEmail bound to emailList and onChangeEmailList with the referentially stable isEmail validator, draws each address through getEmailLabel, and shows an ErrorMessage when isError carries a message.
 *
 * @param props - The email list and its change handler, title and subtitle, error state and an optional remove icon.
 * @returns The rendered multi-email input element.
 */
const MultiEmailInputV2 = (props: MultiEmailInputV2Props) => {
  const {
    isError = false,
    errorMessage = '',
    disabled = false,
    title = '',
    subtitle = '',
    removeIcon = undefined,
    ariaLabel = t`Recipient emails`,
  } = props;
  useMultiEmailInputV2(props);
  const inputId = useId();

  /**
   * Renders one email address as a removable tag inside the multi-email input. Emits the address plus a click target that calls removeEmail with the tag index, using the supplied remove icon or the default small close icon.
   *
   * @param email - The email address shown on the tag.
   * @param index - Position of the address in the list, used as the key and removal argument.
   * @param removeEmail - Callback removing the address at the given index.
   * @returns The tag element for the address.
   */
  const getEmailLabel = (email: any, index: any, removeEmail: any) => (
    <div className="tag-email" data-tag key={index}>
      {email}
      <span data-tag-handle aria-hidden onClick={() => removeEmail(index)}>
        <img src={removeIcon || SmallCloseIcon} alt="remove" />
      </span>
    </div>
  );

  return (
    <div id="multi-emails-input-v2">
      {title ? (
        <label htmlFor={inputId} className="title">
          {title} {subtitle && <span className="sub-title">{subtitle}</span>}
        </label>
      ) : (
        <label htmlFor={inputId} style={VISUALLY_HIDDEN_STYLE}>
          {ariaLabel}
        </label>
      )}
      <ReactMultiEmailAny
        id={inputId}
        className={classNames(
          'flex-1 custom-email-input',
          isError && 'error',
          disabled && 'disabled',
        )}
        emails={props.emailList}
        onChange={props.onChangeEmailList}
        placeholder="Enter emails, comma or space separated"
        // Must stay referentially stable: ReactMultiEmail re-runs its async re-validation of the
        // whole list whenever validateEmail changes identity, and that pass overwrites the tags it
        // holds internally — an inline arrow makes it race with tags the user just added.
        validateEmail={isEmail}
        getLabel={getEmailLabel as any}
      />

      {isError && !!errorMessage && <ErrorMessage errorMessage={errorMessage} hasIcon />}
    </div>
  );
};

export default MultiEmailInputV2;
